import type { KeyboardEvent } from 'react';
import type { LearningsAskThreadResponse } from 'definitions';
import type { UserSummary } from 'pertentodb/types';
import type { AskTurnView } from './learnings-ask-thread';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LEARNINGS_ASK_LIMITS } from 'definitions';
import { meterView } from 'helpers/ai-credits';
import { useClient } from 'hooks/useClient';
import { useGlobal } from 'hooks/useGlobal';
import { mergeTurns, senderName, workingPhase } from '@/state/results-ai/results-ai-thread';
import { ResultsChatError, streamResultsChat } from '@/state/results-ai/results-ai-stream';
import { applyAskEvent, fromStoredTurn, newAskTurn } from './learnings-ask-thread';

export const LEARNINGS_ASK_PANEL_KEY = 'LEARNINGS_ASK_PANEL_OPEN';
export const learningsAskThreadKey = (websiteId: number) => ['LEARNINGS_ASK_THREAD', websiteId];
export const learningsAskLocalKey = (websiteId: number) => ['LEARNINGS_ASK_LOCAL_TURNS', websiteId];

export const LEARNINGS_ASK_INPUT_ID = 'learnings-ask-input';
export const LEARNINGS_ASK_OUT_OF_CREDITS_ID = 'learnings-ask-out-of-credits';

/** Starting points for an empty conversation, sent as they read. */
export const SUGGESTED_QUESTIONS = [
  'What hurts checkout?',
  'Which page responds best to trust?',
  'What should we test next?',
] as const;

/** How often the thread is read again while a colleague's question is being answered... */
export const COLLEAGUE_POLL_MS = 10_000;
/** ...and while the person's own is still being finished (another tab, or saved after Stop). */
export const OWN_POLL_MS = 1_000;

const STOPPED_MESSAGE = 'Stopped. What was written so far is kept; ask again when you are ready.';

type Thread = LearningsAskThreadResponse<UserSummary>;

type UseLearningsAskParams = {
  /** Null while no website is picked. */
  websiteId: number | null;
  /** Learnings are on and the page has loaded: the thread is read only then. */
  enabled: boolean;
};

/**
 * Ask on the Learnings page, built as the results chat is (useResultsAi, P-PER-44): the website's
 * conversation, saved and shared, GET /ask/thread is what everyone sees, and this browser's own turn
 * streams beside it (in the query cache, per website) until the server has stored it. One question
 * runs per website at a time; while someone else's runs, the thread is read again. Owned by the
 * Learnings screen, not the panel, so an answer keeps coming while the reader moves between views
 * or closes the panel; leaving the page stops it.
 *
 * One draft serves both inputs: the field in the row while the panel is closed, and the panel's
 * own once it is open. Sending from the field opens the panel.
 */
export const useLearningsAsk = ({ websiteId, enabled }: UseLearningsAskParams) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const id = websiteId ?? 0;
  const threadKey = learningsAskThreadKey(id);
  const localKey = learningsAskLocalKey(id);
  // Per website: open on one stays open across its views, and never opens itself on another.
  const [open, setOpen] = useGlobal<boolean>([LEARNINGS_ASK_PANEL_KEY, String(id)], false);
  const [draft, setDraftState] = useState('');
  const [noticeError, setNoticeError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  // Named, so the field can read it fresh before it sends (submitFromField), as the results chat does.
  const threadQuery = {
    queryKey: threadKey,
    queryFn: (): Promise<Thread> => client.get(`/learnings/${websiteId}/ask/thread`),
  };

  const { data: local = [] } = useQuery<AskTurnView[]>({
    queryKey: localKey,
    queryFn: () => [],
    enabled: false,
    initialData: [],
    staleTime: Infinity,
  });
  const ownWorking = local.some((turn) => turn.phase === 'working');

  const thread = useQuery<Thread>({
    ...threadQuery,
    enabled: enabled && websiteId !== null,
    // While a question runs that is not this browser's: a colleague's every COLLEAGUE_POLL_MS; the
    // person's own (another tab, or one still being saved after Stop) every second.
    refetchInterval: (query) => {
      const running = query.state.data?.running;
      if (!running || ownWorking) return false;
      return running.mine ? OWN_POLL_MS : COLLEAGUE_POLL_MS;
    },
  });

  const acknowledged = thread.data?.acknowledged ?? false;
  const authors = thread.data?.authors ?? {};
  const stored = (thread.data?.turns ?? []).map((turn) => fromStoredTurn({ turn, authors }));
  const turns = mergeTurns(stored, local);
  const meter = thread.data?.credits ? meterView(thread.data.credits) : null;
  const outOfCredits = !!meter?.exhausted;

  // Through the cache's own updater, never a value captured at render: events arrive faster than renders.
  const updateLocal = (update: (current: AskTurnView[]) => AskTurnView[]) =>
    queryClient.setQueryData<AskTurnView[]>(localKey, (current) => update(current ?? []));
  const updateTurn = (key: string, update: (turn: AskTurnView) => AskTurnView) =>
    updateLocal((current) => current.map((turn) => (turn.key === key ? update(turn) : turn)));

  // Leaving the page stops a running turn. Cleanup only, not data fetching. `mountedRef` lets a
  // question from the field, still reading the thread when the page is left, not start.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const running = thread.data?.running ?? null;
  /** "Anna is asking…", or the person's own question from another tab; null when none is running. */
  const otherAsking =
    running && !ownWorking
      ? running.mine
        ? 'Your earlier question is still being answered…'
        : `${senderName(authors[String(running.senderId)])} is asking…`
      : null;

  const last = turns.at(-1);
  const phase = workingPhase(local.find((turn) => turn.phase === 'working'));
  const busy = ownWorking;
  const trimmed = draft.trim();
  const canSend =
    acknowledged && !busy && !otherAsking && !outOfCredits && trimmed.length >= LEARNINGS_ASK_LIMITS.minChars;

  const setDraft = (value: string) => setDraftState(value.slice(0, LEARNINGS_ASK_LIMITS.messageChars));

  /** Reads the thread again after a turn, then drops this browser's copies the server now has. */
  const refreshThread = async () => {
    await queryClient.invalidateQueries({ queryKey: threadKey });
    const known = new Set((queryClient.getQueryData<Thread>(threadKey)?.turns ?? []).map((turn) => turn.turnId));
    updateLocal((current) => current.filter((turn) => !turn.turnId || !known.has(turn.turnId)));
  };

  const run = async (key: string, question: string) => {
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      await streamResultsChat({
        baseUrl: client.baseUrl,
        token: client.token,
        path: `/learnings/${websiteId}/ask`,
        body: { question },
        signal: controller.signal,
        onEvent: (event) => updateTurn(key, (turn) => applyAskEvent(turn, event)),
      });
      // A stream that ended without `done` (the connection dropped) still has to end the turn.
      updateTurn(key, (turn) =>
        turn.phase === 'working'
          ? {
              ...turn,
              phase: 'errored',
              error: { message: 'The answer was cut off. Please try again.', retryable: true },
            }
          : turn,
      );
    } catch (error) {
      if (controller.signal.aborted) {
        updateTurn(key, (turn) => ({ ...turn, phase: 'stopped' }));
      } else {
        const failure =
          error instanceof ResultsChatError
            ? { message: error.message, retryable: error.retryable, status: error.status }
            : { message: 'Something went wrong. Please try again.', retryable: true };
        updateTurn(key, (turn) => ({ ...turn, phase: 'errored', error: failure }));
        // Refused before it started (someone else is asking, a rate limit): the question was never
        // asked, so it goes back into the box rather than being retyped.
        if (error instanceof ResultsChatError && (error.status === 409 || error.status === 429)) {
          setDraftState((current) => (current.trim() ? current : question));
        }
      }
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      // The stored turn, with its checked citations, replaces this browser's copy once read again.
      void refreshThread();
    }
  };

  // The ref, not only `busy`: two asks in one render (a double click) would both see busy false.
  const canAsk = () => !busy && !controllerRef.current && !otherAsking && acknowledged && !outOfCredits;

  /** Starts a turn without the checks: callers decide whether one may start. */
  const start = (text: string) => {
    const turn = newAskTurn({ key: crypto.randomUUID(), question: text });
    updateLocal((current) => [...current, turn]);
    setDraftState((current) => (current.trim() === text ? '' : current));
    void run(turn.key, text);
  };

  const ask = (question: string) => {
    const text = question.trim();
    if (text.length < LEARNINGS_ASK_LIMITS.minChars || !canAsk()) return;
    start(text);
  };

  const submit = () => ask(draft);

  /**
   * The row's field: the panel opens with the question asked. When it cannot be sent yet (the notice,
   * a colleague asking, no credits, too short) it waits in the panel's box, typed. Decided on the
   * thread as just read, not this render's copy, which may be stale.
   */
  const submitFromField = async () => {
    const question = draft.trim();
    if (!question) return;
    setOpen(true);
    const fresh = await queryClient.fetchQuery(threadQuery).catch(() => null);
    if (!mountedRef.current || controllerRef.current || !fresh?.acknowledged || fresh.running) return;
    if (fresh.credits && meterView(fresh.credits).exhausted) return;
    if (question.length < LEARNINGS_ASK_LIMITS.minChars) return;
    start(question);
  };

  /** A failed question asked again, as a new turn; only when it can be sent now, so it is never lost. */
  const retry = (key: string) => {
    const turn = turns.find((candidate) => candidate.key === key);
    if (!turn || !canAsk()) return;
    updateLocal((current) => current.filter((candidate) => candidate.key !== key));
    ask(turn.question);
  };

  const stop = () => controllerRef.current?.abort();

  /** A refusal (an account read-only until billing is sorted out, a network error) is said in the notice. */
  const acknowledge = async () => {
    setNoticeError(null);
    try {
      await client.post(`/learnings/${websiteId}/ask/acknowledge`);
      queryClient.setQueryData<Thread>(threadKey, (current) =>
        current ? { ...current, acknowledged: true } : current,
      );
    } catch (error) {
      const status = typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;
      setNoticeError(
        status === 402
          ? 'This account is read-only until its billing is sorted out, so Ask cannot be used yet.'
          : 'That did not go through. Please try again.',
      );
    }
  };

  /** Enter sends, Shift+Enter adds a line, Esc stops a running turn and otherwise closes the panel. */
  const onInputKeyDown = (ev: KeyboardEvent<HTMLTextAreaElement>) => {
    // Composing a word with an IME: Safari reports it only as keyCode 229 (see AiAskField).
    if (ev.nativeEvent.isComposing || ev.keyCode === 229) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (busy) stop();
      else setOpen(false);
      return;
    }
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      if (canSend) submit();
    }
  };

  /** What the line beside Send says. */
  const statusLine = busy
    ? phase === 'writing'
      ? 'Writing…'
      : 'Thinking…'
    : (otherAsking ??
      (trimmed.length > 0 && trimmed.length < LEARNINGS_ASK_LIMITS.minChars
        ? `At least ${LEARNINGS_ASK_LIMITS.minChars} characters`
        : 'Enter to send, Shift+Enter for a new line'));

  return {
    acknowledged,
    open,
    setOpen,
    toggle: () => setOpen(!open),
    isLoading: thread.isLoading,
    isError: thread.isError,
    reload: () => void thread.refetch(),
    noticeError,
    turns,
    last,
    draft,
    setDraft,
    phase,
    busy,
    otherAsking,
    canSend,
    outOfCredits,
    meter,
    billingUrl: thread.data?.billingUrl ?? null,
    statusLine,
    stoppedMessage: STOPPED_MESSAGE,
    ask,
    submit,
    submitFromField: () => void submitFromField(),
    retry,
    stop,
    acknowledge,
    onInputKeyDown,
  };
};

export type LearningsAskManager = ReturnType<typeof useLearningsAsk>;
